import { useState, useEffect } from 'react';
import { X, Trophy, Star, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const PODIUM_COLORS = [
  { bg: 'from-amber-400 to-yellow-500', text: 'text-amber-900', badge: 'bg-amber-100 text-amber-800', label: '1st', ring: 'ring-amber-300' },
  { bg: 'from-slate-300 to-slate-400', text: 'text-slate-800', badge: 'bg-slate-100 text-slate-700', label: '2nd', ring: 'ring-slate-300' },
  { bg: 'from-orange-300 to-orange-400', text: 'text-orange-900', badge: 'bg-orange-100 text-orange-800', label: '3rd', ring: 'ring-orange-300' },
];

const CONFETTI_COLORS = ['#fbbf24', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6', '#10b981', '#ec4899', '#f97316'];

const ConfettiPiece = ({ delay, left }) => {
  const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
  const size = 6 + Math.random() * 8;
  const duration = 2 + Math.random() * 2;
  const rotation = Math.random() * 360;

  return (
    <div
      className="absolute top-0 pointer-events-none"
      style={{
        left: `${left}%`,
        width: size,
        height: size * (0.5 + Math.random()),
        backgroundColor: color,
        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
        animation: `confetti-fall ${duration}s ease-in ${delay}s forwards`,
        transform: `rotate(${rotation}deg)`,
        opacity: 0,
      }}
    />
  );
};

export const StudentAppreciationOverlay = ({ behaviourLogs, students, onClose }) => {
  const [revealIndex, setRevealIndex] = useState(-1);
  const [showConfetti, setShowConfetti] = useState(false);

  // Calculate top 3 students by positive points
  const studentPoints = {};
  const studentReasons = {};

  behaviourLogs
    .filter(l => l.log_type === 'positive')
    .forEach(l => {
      const sid = l.student_id;
      studentPoints[sid] = (studentPoints[sid] || 0) + 1;
      if (!studentReasons[sid]) studentReasons[sid] = [];
      if (l.description && !studentReasons[sid].includes(l.description)) {
        studentReasons[sid].push(l.description);
      }
    });

  const ranked = Object.entries(studentPoints)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([sid, points], idx) => {
      const student = students.find(s => s.student_id === sid);
      return {
        name: student?.name || 'Unknown',
        points,
        reasons: studentReasons[sid] || [],
        rank: idx,
      };
    });

  // Reveal animation: 3rd → 2nd → 1st
  const revealOrder = [...ranked].reverse();

  useEffect(() => {
    if (ranked.length === 0) return;
    const timers = [];
    revealOrder.forEach((_, i) => {
      timers.push(setTimeout(() => setRevealIndex(i), 1500 + i * 2000));
    });
    // Show confetti after all revealed
    timers.push(setTimeout(() => setShowConfetti(true), 1500 + revealOrder.length * 2000));
    return () => timers.forEach(clearTimeout);
  }, [ranked.length]);

  if (ranked.length === 0) {
    return (
      <div className="fixed inset-0 z-[10000] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center" data-testid="appreciation-overlay">
        <div className="text-center">
          <Star className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">No Positive Points Yet</h2>
          <p className="text-slate-400 mb-6">Log some positive behaviour first!</p>
          <Button onClick={onClose} className="bg-white/20 hover:bg-white/30 text-white rounded-full px-8 h-12" data-testid="close-appreciation-btn">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex flex-col items-center justify-center overflow-hidden" data-testid="appreciation-overlay">
      {/* Confetti */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 60 }, (_, i) => (
            <ConfettiPiece key={i} delay={Math.random() * 0.5} left={Math.random() * 100} />
          ))}
        </div>
      )}

      {/* Title */}
      <div className="text-center mb-10 animate-fade-in">
        <div className="inline-flex items-center gap-3 mb-4">
          <Sparkles className="w-8 h-8 text-amber-400" />
          <Trophy className="w-10 h-10 text-amber-400" />
          <Sparkles className="w-8 h-8 text-amber-400" />
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white mb-2">
          Star Students
        </h1>
        <p className="text-lg text-white/60 font-heading">Today's top performers</p>
      </div>

      {/* Podium */}
      <div className="flex items-end gap-4 sm:gap-8 mb-10 px-4">
        {revealOrder.map((student, revealIdx) => {
          const isRevealed = revealIdx <= revealIndex;
          const podiumIdx = student.rank;
          const colors = PODIUM_COLORS[podiumIdx];
          const heights = ['h-48 sm:h-56', 'h-40 sm:h-44', 'h-32 sm:h-36'];

          return (
            <div
              key={student.name}
              className={`flex flex-col items-center transition-all duration-700 ${
                isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${revealIdx * 200}ms` }}
              data-testid={`podium-${podiumIdx}`}
            >
              {/* Student info */}
              <div className={`text-center mb-3 transition-all duration-500 ${isRevealed ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br ${colors.bg} flex items-center justify-center text-2xl sm:text-3xl font-black text-white shadow-xl ring-4 ${colors.ring} mx-auto mb-2`}>
                  {student.name.charAt(0)}
                </div>
                <h3 className="text-lg sm:text-xl font-heading font-bold text-white mb-1">{student.name}</h3>
                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full ${colors.badge} text-sm font-bold`}>
                  <Star className="w-3.5 h-3.5" /> {student.points} point{student.points !== 1 ? 's' : ''}
                </div>
                {student.reasons.length > 0 && (
                  <div className="mt-2 max-w-[180px]">
                    {student.reasons.slice(0, 2).map((r, i) => (
                      <p key={i} className="text-xs text-white/50 leading-snug truncate">{r}</p>
                    ))}
                  </div>
                )}
              </div>

              {/* Podium block */}
              <div className={`w-24 sm:w-32 ${heights[podiumIdx]} rounded-t-xl bg-gradient-to-t ${colors.bg} flex items-start justify-center pt-3 shadow-lg transition-all duration-500 ${isRevealed ? 'opacity-100' : 'opacity-0'}`}>
                <span className="text-2xl sm:text-3xl font-heading font-black text-white/80">{colors.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Close button */}
      <Button
        onClick={onClose}
        className="bg-white/20 hover:bg-white/30 text-white rounded-full px-8 h-12 text-base font-medium backdrop-blur-sm"
        data-testid="close-appreciation-btn"
      >
        End Celebration
      </Button>

      <button
        onClick={onClose}
        className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white/60"
      >
        <X className="w-6 h-6" />
      </button>

      <style>{`
        @keyframes confetti-fall {
          0% { opacity: 1; transform: translateY(-10px) rotate(0deg); }
          100% { opacity: 0; transform: translateY(100vh) rotate(720deg); }
        }
      `}</style>
    </div>
  );
};
